import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import type { Hit, Ref } from '../api'
import { api } from '../api'

type Props = {
  id: number
  index: number
  count: number
  onClose: () => void
  onNav: (dir: -1 | 1) => void
  onOpen: (id: number) => void
  onFilter: (field: string, ref: Ref) => void
  picked: boolean
  onPick: (h: Hit) => void
  onLicense: (id: number) => void
}

const INTERNAL_LABELS: Record<string, string> = {
  standort: 'Standort', standort_voruebergehend: 'Standort (vorübergehend)', standort_anmerkung: 'Standort-Anmerkung',
  zustand: 'Zustand', handlungsempfehlung: 'Handlungsempfehlung', bewertung: 'Bewertung',
  ki_herkunft: 'KI-Herkunft', ki_erschliessung: 'KI-Erschließung', ki_konfidenz: 'KI-Konfidenz',
}

export function Drawer({ id, index, count, onClose, onNav, onOpen, onFilter, picked, onPick, onLicense }: Props) {
  const { data: d, error, isLoading } = useQuery({ queryKey: ['object', id], queryFn: () => api.object(id) })
  const { data: related } = useQuery({ queryKey: ['related', id], queryFn: () => api.related(id), enabled: !!d })
  const closeBtn = useRef<HTMLButtonElement>(null)
  const [cite, setCite] = useState<string | null>(null)

  useEffect(() => {
    closeBtn.current?.focus()
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [])
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement) return
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowRight') onNav(1)
      if (e.key === 'ArrowLeft') onNav(-1)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, onNav])
  useEffect(() => setCite(null), [id])

  const big = d?.images.huge ?? d?.images.preview ?? d?.images.small
  const persons = d?.persons ?? []
  const faces = splitFaces(d?.faces ?? [], persons)
  const tag = (field: string, r: Ref, label?: string) => (
    <button key={`${field}${r.id}`} title="Zur Suche hinzufügen" onClick={() => onFilter(field, r)}>{label ?? r.label}</button>
  )

  function copyCite() {
    if (!d) return
    const text = `Stiftung Günter Zint, ${d.inv}: ${d.title}${d.date ? ` (${d.date})` : ''}. ${window.location.origin}/?q=${encodeURIComponent('inv:' + d.inv)}`
    if (!navigator.clipboard) { setCite(text); return }
    navigator.clipboard.writeText(text).then(() => setCite(`Kopiert: ${text}`), () => setCite(text))
  }

  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div className="drawer" role="dialog" aria-modal="true" aria-label={d?.title ?? 'Objekt'}>
        <div className="dhead">
          <span className="mono">{d?.inv ?? '…'}</span>
          <div className="nav">
            {index >= 0 && <span className="muted">{index + 1} / {count}</span>}
            <button className="b" onClick={() => onNav(-1)} disabled={index <= 0} aria-label="Vorheriges Objekt">←</button>
            <button className="b" onClick={() => onNav(1)} disabled={index < 0 || index >= count - 1} aria-label="Nächstes Objekt">→</button>
            <button className="b" ref={closeBtn} onClick={onClose}>Schließen</button>
          </div>
        </div>
        <div className="dbody">
          {isLoading && <p className="muted">Lade …</p>}
          {error && <div className="banner">{(error as Error).message}</div>}
          {d && (
            <>
              <div>
                <div className="dimg">
                  {big ? <img src={big.url} alt={d.title} /> : <span className="muted">kein Bild freigegeben</span>}
                  <span className="wm">Stiftung Günter Zint</span>
                </div>
                <p className="note">Vorschau. Das Original wird nach dem Lizenzkauf geliefert.</p>
              </div>
              <div className="dmeta">
                <span className={`badge ${d.can_license ? 'L' : 'R'}`}>{d.can_license ? 'Online lizenzierbar' : 'Lizenz auf Anfrage'}</span>
                <h2>{d.title}</h2>
                {d.description && <div className="muted">{d.description}</div>}
                <dl>
                  <dt>Datierung</dt><dd className="mono">{d.date || 'ohne Datierung'}</dd>
                  {d.recorded && <><dt>Dateidatum</dt><dd className="mono">{d.recorded}</dd></>}
                  {d.photographer && <><dt>Fotograf/in</dt><dd><div className="tagl">{tag('fotograf', d.photographer)}</div></dd></>}
                  <dt>Personen</dt><dd><div className="tagl">{persons.length ? persons.map((p) => tag('person', p)) : <span className="muted">keine benannt</span>}</div></dd>
                  {faces.named.length + faces.unnamed > 0 && <><dt>Gesichter (KI)</dt><dd><div className="tagl">{faces.named.map((p) => tag('gesicht', p))}{faces.unnamed > 0 && <span className="muted" style={{ fontSize: 'var(--t-xs)', alignSelf: 'center' }}>+ {faces.unnamed} unbenannte</span>}</div></dd></>}
                  <dt>Orte</dt><dd><div className="tagl">{d.places.length ? d.places.map((p) => tag('ort', p, (p.path ?? []).slice(1).join(' › ') || p.label)) : '–'}</div></dd>
                  <dt>Schlagwörter</dt><dd><div className="tagl">{d.keywords.length ? d.keywords.map((p) => tag('schlagwort', p, (p.path ?? [p.label]).join(' › '))) : '–'}</div></dd>
                  {d.gnd.length > 0 && <><dt>GND</dt><dd><div className="tagl">{d.gnd.map((g) => <a key={g.uri} href={g.uri} target="_blank" rel="noreferrer">{g.label}</a>)}</div></dd></>}
                  <dt>Sammlung</dt><dd><div className="tagl">{d.collections.map((c) => tag('sammlung', c, (c.path ?? [c.label]).join(' › ')))}</div></dd>
                  {d.techniques.length > 0 && <><dt>Technik</dt><dd>{d.techniques.map((t) => t.label).join(', ')}</dd></>}
                  {d.old_titles.length > 0 && <><dt>Frühere Titel</dt><dd>{d.old_titles.join(' · ')}</dd></>}
                  {d.copyright.length > 0 && <><dt>Rechte</dt><dd>{d.copyright.join(', ')}</dd></>}
                  {d.provenance.length > 0 && <><dt>Provenienz</dt><dd>{d.provenance.join(', ')}</dd></>}
                  {d.license_note && <><dt>Lizenzhinweis</dt><dd>{d.license_note}</dd></>}
                  <dt>Bildnachweis</dt><dd>Foto: {d.photographer?.label || 'Fotograf/in unbekannt'} / Stiftung Günter Zint</dd>
                  {d.internal && Object.entries(d.internal).filter(([, v]) => v !== null && v !== '').map(([k, v]) => (
                    <div key={k} style={{ display: 'contents' }}><dt className="internal">{INTERNAL_LABELS[k] ?? k}</dt><dd className="internal">{String(v)}</dd></div>
                  ))}
                </dl>
                <div className="actions">
                  <button className="btn primary" onClick={() => onLicense(d.id)}>{d.can_license ? 'Lizenz berechnen' : 'Lizenz anfragen'}</button>
                  <button className="btn" onClick={() => onPick(d)}>{picked ? 'Vom Leuchttisch nehmen' : 'Auf den Leuchttisch'}</button>
                  <button className="btn" onClick={copyCite}>Zitierlink kopieren</button>
                </div>
                {cite && <p className="note">{cite}</p>}
              </div>
              {related && related.length > 0 && (
                <div className="related">
                  <div className="label" style={{ marginBottom: 8 }}>Verwandte Objekte · gleiche Personen oder gleiches Ereignis</div>
                  <div className="strip">
                    {related.map((h) => (
                      <button key={h.id} onClick={() => onOpen(h.id)} title={`${h.title} (${h.date})`}>
                        {h.images.small && <img src={h.images.small.url} alt={h.title} loading="lazy" />}
                        <span>{h.year ?? ''}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  )
}

/** Faces not already listed as persons; unknown people (flagged in easydb) are only counted. */
function splitFaces(faces: (Ref & { unnamed?: boolean })[], persons: Ref[]) {
  const seen = new Set(persons.map((p) => p.id))
  const named: Ref[] = []
  const unnamed = new Set<number | null>()
  for (const f of faces) {
    if (f.unnamed) { unnamed.add(f.id); continue }
    if (seen.has(f.id)) continue
    seen.add(f.id)
    named.push(f)
  }
  return { named, unnamed: unnamed.size }
}
